const BATCH_DEPOSIT_EVENT_NAME = "BatchDckslapDepositEvent";

const unwrapGatewayValue = (value) => {
    if (value === null || value === undefined) {
        return null;
    }

    if (
        typeof value === "string" ||
        typeof value === "number" ||
        typeof value === "boolean"
    ) {
        return value;
    }

    if (Array.isArray(value)) {
        return value.map((entry) => unwrapGatewayValue(entry));
    }

    if (typeof value === "object") {
        if ("value" in value) {
            return unwrapGatewayValue(value.value);
        }

        if ("address" in value) {
            return unwrapGatewayValue(value.address);
        }

        if ("entity_address" in value) {
            return unwrapGatewayValue(value.entity_address);
        }

        if ("global_address" in value) {
            return unwrapGatewayValue(value.global_address);
        }
    }

    return null;
};

const getEventFieldValue = (fields, targetNames) => {
    if (!Array.isArray(fields)) {
        return null;
    }

    const field = fields.find(
        (entry) =>
            entry &&
            typeof entry.field_name === "string" &&
            targetNames.includes(entry.field_name)
    );

    if (!field) {
        return null;
    }

    return unwrapGatewayValue(field.value);
};

const toNumber = (value) => {
    const unwrapped = unwrapGatewayValue(value);

    if (typeof unwrapped === "number") {
        return Number.isFinite(unwrapped) ? unwrapped : null;
    }

    if (typeof unwrapped === "string") {
        const parsed = Number(unwrapped);
        return Number.isFinite(parsed) ? parsed : null;
    }

    return null;
};

export const inferAddressNetwork = (address) => {
    if (typeof address !== "string") {
        return null;
    }

    if (address.includes("_tdx_")) {
        return "stokenet";
    }

    if (address.includes("_rdx")) {
        return "mainnet";
    }

    return null;
};

export const validateBatchConfig = ({
    componentAddress,
    dckslapAddress,
    reddicksAddress,
    xrdAddress,
    accountAddress,
}) => {
    if (!componentAddress || typeof componentAddress !== "string") {
        return "Batch component is not configured for this network.";
    }

    if (componentAddress.includes("PLACEHOLDER")) {
        return "Batch component address is invalid.";
    }

    const componentNetwork = inferAddressNetwork(componentAddress);
    const accountNetwork = inferAddressNetwork(accountAddress);
    const dckslapNetwork = inferAddressNetwork(dckslapAddress);
    const reddicksNetwork = inferAddressNetwork(reddicksAddress);
    const xrdNetwork = inferAddressNetwork(xrdAddress);

    if (componentNetwork && accountNetwork && componentNetwork !== accountNetwork) {
        return "Connected account network does not match batch component network.";
    }

    if (componentNetwork && dckslapNetwork && componentNetwork !== dckslapNetwork) {
        return "DCKSLAP address network does not match batch component network.";
    }

    if (componentNetwork && reddicksNetwork && componentNetwork !== reddicksNetwork) {
        return "REDDICKS address network does not match batch component network.";
    }

    if (componentNetwork && xrdNetwork && componentNetwork !== xrdNetwork) {
        return "XRD address network does not match batch component network.";
    }

    return null;
};

export const parseBatchDepositEvent = (event) => {
    if (!event || event.identifier?.event !== BATCH_DEPOSIT_EVENT_NAME) {
        return null;
    }

    const fields = event.payload?.programmatic_json?.fields;

    if (!Array.isArray(fields)) {
        return null;
    }

    const accountValue = getEventFieldValue(fields, ["account"]);
    const choiceValue = getEventFieldValue(fields, ["choice"]);
    const depositedAmountValue = getEventFieldValue(fields, [
        "deposited_amount",
        "depositedAmount",
    ]);

    const account = typeof accountValue === "string" ? accountValue : null;
    const choice = toNumber(choiceValue);
    const depositedAmount = toNumber(depositedAmountValue);

    if (!account || choice === null || depositedAmount === null) {
        return null;
    }

    return {
        account,
        choice,
        depositedAmount,
        eventName: BATCH_DEPOSIT_EVENT_NAME,
    };
};

export const findMatchingBatchDepositEvent = (transaction, expected) => {
    const events = transaction?.receipt?.detailed_events;

    if (!Array.isArray(events)) {
        return null;
    }

    for (const event of events) {
        const parsed = parseBatchDepositEvent(event);

        if (!parsed) {
            continue;
        }

        if (
            parsed.account === expected.account &&
            Number(parsed.choice) === Number(expected.choice) &&
            Number(parsed.depositedAmount) === Number(expected.depositedAmount)
        ) {
            return parsed;
        }
    }

    return null;
};

export const findAllMatchingBatchDepositEvents = (transactions, expected, seenTxHashes) => {
    const results = [];

    for (const tx of transactions) {
        const txHash = tx.intent_hash;

        if (!txHash || seenTxHashes.has(txHash)) {
            continue;
        }

        const events = tx?.receipt?.detailed_events;

        if (!Array.isArray(events)) {
            continue;
        }

        for (const event of events) {
            const parsed = parseBatchDepositEvent(event);

            if (!parsed) {
                continue;
            }

            if (
                parsed.account === expected.account &&
                Number(parsed.choice) === Number(expected.choice) &&
                Number(parsed.depositedAmount) === Number(expected.depositedAmount)
            ) {
                results.push({ parsedEvent: parsed, transaction: tx });
                seenTxHashes.add(txHash);
                break;
            }
        }
    }

    return results;
};

export const extractBatchRewards = (transaction, accountAddress, candyResourceMap) => {
    const fungibleChanges = transaction?.balance_changes?.fungible_balance_changes;

    if (!Array.isArray(fungibleChanges) || fungibleChanges.length === 0) {
        return [];
    }

    const receivedResources = fungibleChanges.filter(
        (fc) => fc.entity_address === accountAddress
    );

    return receivedResources
        .filter((resource) => {
            const change = Number(resource.balance_change);
            return Number.isFinite(change) && change > 0;
        })
        .map((resource) => {
            const candyInfo = candyResourceMap?.[resource.resource_address];
            return {
                name: candyInfo ? candyInfo.name : "Unknown",
                rarity: candyInfo ? candyInfo.rarity : "Common",
                amount: Number(resource.balance_change),
                resourceAddress: resource.resource_address,
            };
        })
        .filter((reward) => reward.name !== "Unknown");
};

